import {
  BACKLOG_CAP, CLOSURE_FACTOR, DEMAND_GROWTH, GLUT_FACTOR, GOODS, PRODUCER_BUILD_COST,
  PRODUCER_UPGRADE_STEP,
  RECIPE, REFINERY_RATE, TRUCK_CAPACITY, TRUCK_COST, TRUCK_REFUND, TRUCK_UPKEEP,
} from "../core/constants";
import {
  clamp, closedRoute, findNode, findRoute,
  hasMilestone,
  corridorLoad, laneCost, laneUpkeep, narrowest, roundTripAt,
  routeCapacity, routeCost, routeFactor, routeLength, throughputAt, viaOptions,
  canUpgrade, producerUpgradeCost, sinkOf, sourceOf, totalBacklog, totalDemand,
  totalStock,
  trucksOnRoute,
} from "../core/sim";
import type { ReplayAction } from "../core/replay";
import type { GameState, GoodId } from "../core/types";
import { wantedGoods } from "../core/world";
import { jamLevel } from "../render/canvas";
import { goodColor, palette } from "../render/palette";
import { coarsePointer, euro, int, need, pct, rate } from "./format";
import { expandSheet } from "./sheet";
import type { UiState } from "./state";
import { selectionKey } from "./state";

/**
 * The goods a card lists. Every good gets a row, wanted or not — „Erz —" is the
 * sentence RFC-0016 is about. Werkzeug is the exception until a refinery exists
 * on the map (RFC-0015 phase 1 adds the good, not a source for it): a row that
 * can only ever read 0 advertises a mechanic the game does not have yet.
 *
 * Asked of the world rather than of the node, so the day refining ships, a city
 * that does not want werkzeug still says so instead of hiding the row.
 */
const listedGoods = (s: GameState): GoodId[] => {
  const refines = s.nodes.some((n) => n.kind === "transfer" && n.refines);
  return (Object.keys(GOODS) as GoodId[]).filter((g) => g !== "werkzeug" || refines);
};

/**
 * Seitenpanel. Baut das Markup nur neu, wenn sich die Auswahl ändert;
 * die Zahlen darin werden pro Frame über gespeicherte Referenzen aktualisiert.
 * Sonst würden Buttons bei jedem Frame ihre Klick-Handler verlieren.
 */
export class Panel {
  private root = need("panel");
  private lastKey = "";
  private updaters: (() => void)[] = [];

  constructor(private readonly ui: UiState, private readonly runCommand: (action: ReplayAction) => boolean) {}

  /** Erzwingt Neuaufbau, z. B. nachdem eine Strecke abgerissen wurde. */
  invalidate(): void {
    this.lastKey = "";
  }

  sync(s: GameState): void {
    const key = this.ui.linkFrom !== null
      ? `link:${this.ui.linkFrom}:${this.ui.linkGood}:${this.ui.linkTo}`
      : selectionKey(this.ui.selection);

    if (key !== this.lastKey) {
      this.lastKey = key;
      this.updaters = [];
      this.build(s);
      // Drives the bottom sheet on a narrow screen: nothing selected, no sheet,
      // so the map starts unobstructed. Ignored by the desktop layout.
      //
      // Never while a link is pending, and that is not a detail. Opening the
      // sheet shortens the map, which moves every node on it — measured at 26 px
      // on a 390 px phone, against a hit radius of about 13. The player taps a
      // producer, the target city jumps out from under their finger, and the
      // second tap of a two-tap gesture misses. The linking hint lives in the
      // statusbar, which needs no space the map was using.
      const open = key !== "none" && this.ui.linkFrom === null;
      this.root.closest(".sidebar")?.setAttribute("data-open", String(open));
      // A new selection un-parks the sheet: the player asked to see this one.
      if (open) expandSheet();
    }
    for (const u of this.updaters) u();
  }

  // ── Aufbau ───────────────────────────────────────────────────────────────

  private build(s: GameState): void {
    if (this.ui.linkFrom !== null && this.ui.linkTo !== null) return this.buildLinkChoice(s);
    if (this.ui.linkFrom !== null) return this.buildLinking(s);
    const sel = this.ui.selection;
    if (!sel) return this.buildEmpty();
    if (sel.kind === "node") return this.buildNode(s, sel.id);
    return this.buildRoute(s, sel.id);
  }

  private buildLinking(s: GameState): void {
    const from = findNode(s, this.ui.linkFrom!);
    const verb = coarsePointer() ? "antippen" : "anklicken";
    // Out of a hub the good is already chosen, and naming it is the only thing
    // that tells the player which of the two buttons they pressed.
    const cargo = this.ui.linkGood !== null ? ` (${GOODS[this.ui.linkGood].name})` : "";
    // The cancel button is unconditional. A finger has no Escape key, which is
    // what forces it — but a visible way out is better on a mouse too, so the
    // hint keeps its keyboard line alongside rather than instead.
    this.root.innerHTML = `
      <div class="hint-body">
        Strecke ab <strong>${from.name}</strong>${cargo} — jetzt eine
        <strong>Stadt</strong> oder einen <strong>Umschlagplatz</strong> ${verb}.
        ${coarsePointer() ? "" : "<br><br><kbd>Esc</kbd> bricht ab."}
      </div>
      <button class="action secondary" id="link-cancel">Abbrechen</button>`;
    need("link-cancel").addEventListener("click", () => {
      this.ui.linkFrom = null;
      this.ui.linkGood = null;
      this.ui.selection = null;
      this.invalidate();
    });
  }

  /**
   * The one decision RFC-0033 adds: a straight road of your own, or a cheaper
   * path over asphalt that already exists and whose capacity you then share.
   *
   * It shows the price, the detour and how busy the shared legs are — and stops
   * there. Which of the two is right depends on traffic that has not happened
   * yet, and computing it for the player is the thing §4 refuses to do.
   */
  private buildLinkChoice(s: GameState): void {
    const from = findNode(s, this.ui.linkFrom!);
    const to = findNode(s, this.ui.linkTo!);
    const options = viaOptions(s, from.id, to.id).slice(0, 2);
    const direct = routeCost(from, to);

    const rows = options.map((o, i) => {
      const via = findNode(s, o.via);
      return `<button class="action secondary" id="link-via-${i}" data-via="${o.via}">
        über ${via.name} — ${euro(o.cost)}<br>
        <small>${Math.round((o.detour - 1) * 100)} % Umweg, teilt sich die Straße mit
        ${o.shares} ${o.shares === 1 ? "Strecke" : "Strecken"}</small>
      </button>`;
    }).join("");

    this.root.innerHTML = `
      <div class="hint-body">
        <strong>${from.name}</strong> → <strong>${to.name}</strong>: es führt schon
        Asphalt in die Richtung. Mitbenutzen ist billiger und teilt sich den Stau.
      </div>
      <button class="action" id="link-direct">eigene Strecke — ${euro(direct)}</button>
      ${rows}
      <button class="action secondary" id="link-cancel">Abbrechen</button>`;

    const finish = (action: ReplayAction) => {
      const routeId = s.nextRouteId;
      this.ui.selection = this.runCommand(action) ? { kind: "route", id: routeId } : null;
      this.ui.linkFrom = null;
      this.ui.linkGood = null;
      this.ui.linkTo = null;
      this.invalidate();
    };
    need("link-direct").addEventListener("click", () => {
      finish({ type: "buildRoute", from: from.id, to: to.id, good: this.ui.linkGood ?? undefined });
    });
    options.forEach((o, i) => {
      need(`link-via-${i}`).addEventListener("click", () => {
        finish({ type: "buildRouteVia", from: from.id, to: to.id, via: o.via, good: this.ui.linkGood ?? undefined });
      });
    });
    need("link-cancel").addEventListener("click", () => {
      this.ui.linkFrom = null;
      this.ui.linkGood = null;
      this.ui.linkTo = null;
      this.ui.selection = null;
      this.invalidate();
    });
  }

  private buildEmpty(): void {
    const verb = coarsePointer() ? "antippen" : "anklicken";
    this.root.innerHTML = `
      <div class="hint-body">
        <strong>Produzent</strong> ${verb}, dann eine <strong>Stadt</strong> — das baut eine Strecke.
        <br><br>
        Strecke ${verb}, um <strong>Lkw</strong> zu kaufen.
        <br><br>
        Städte stauen unbediente Nachfrage auf. Sinkt ihr Vertrauen, zahlen sie weniger.
        Die Nachfrage wächst täglich um
        <strong>${Math.round((DEMAND_GROWTH - 1) * 100)} %</strong> bis zu einer Obergrenze.
      </div>`;
  }

  private buildNode(s: GameState, id: number): void {
    const n = findNode(s, id);

    if (n.kind === "producer") {
      const good = GOODS[n.good];
      const color = goodColor(good.token);
      const serving = s.routes.filter((r) => r.from === n.id).length;
      const cost = producerUpgradeCost(n.level);
      // A works the world has closed (RFC-0017 §3.5). No flag on the node: a
      // rate of 0 *is* the ruin, because the generator never makes one — which
      // is also why no save version had to move for this.
      const closed = n.rate <= 0;
      this.root.innerHTML = `
        <div class="card">
          <div class="card-title"><span class="swatch" style="background:${color}"></span>${n.name}</div>
          <div class="kv"><span>Ware</span><strong>${good.name}${closed ? " · stillgelegt" : ""}</strong></div>
          <div class="kv"><span>Erlös</span><strong>${good.price} € / Einheit</strong></div>
          <div class="kv"><span>Produktion</span><strong id="p-rate"></strong></div>
          <div class="meter"><i id="p-meter" style="background:${color}"></i></div>
          <div class="kv"><span>Lager</span><strong id="p-stock"></strong></div>
          <div class="kv"><span>Strecken</span><strong>${serving}</strong></div>
          <button class="action" id="p-upgrade"${closed ? " disabled" : ""}>
            Ausbauen · +${PRODUCER_UPGRADE_STEP.toFixed(1)}/s · ${euro(cost)}
          </button>
          <div class="hint-body" id="p-upgrade-why">${closed
            ? "Dieses Werk ist stillgelegt. Es nimmt kein Geld mehr an — die "
              + "Antwort darauf ist ein neues Werk, nicht dieses."
            : ""}</div>
          <button class="action secondary" id="p-build">
            Neues Werk · ${good.name} · ${euro(PRODUCER_BUILD_COST)}
          </button>
          <div class="hint-body" id="p-build-why"></div>
        </div>`;
      const meter = need("p-meter");
      const stock = need("p-stock");
      const rateEl = need("p-rate");
      const upgrade = need<HTMLButtonElement>("p-upgrade");
      const why = need("p-upgrade-why");
      // The second lever sits on the same card as the first, because it is the
      // answer to the first one's refusal: when the road is the bottleneck, more
      // output *here* is the wrong purchase and a new works elsewhere is the
      // right one (RFC-0017 §3.4). Where it lands is the generator's decision.
      const build = need<HTMLButtonElement>("p-build");
      upgrade.addEventListener("click", () => {
        if (this.runCommand({ type: "upgradeProducer", nodeId: n.id })) this.invalidate();
      });
      // The only refusal money cannot explain, and it needs saying: the
      // generator owns the placement, so a full board is a "no" the player
      // cannot see coming from the price.
      const buildWhy = need("p-build-why");
      build.addEventListener("click", () => {
        if (this.runCommand({ type: "buildProducer", good: n.good })) this.invalidate();
        else {
          buildWhy.textContent = "Dafür ist kein Platz mehr: entweder ist der "
            + "Streifen am Rand voll, oder keine Stadt will diese Ware.";
        }
      });
      this.updaters.push(() => {
        const f = n.stock / n.cap;
        meter.style.width = `${f * 100}%`;
        meter.style.background = f > 0.96 ? palette().error : color;
        stock.textContent = `${Math.floor(n.stock)} / ${n.cap}`;
        rateEl.textContent = rate(n.rate);
        // Two different refusals, and the player has to be able to tell them
        // apart: money is a wait, the road is a different purchase entirely
        // (RFC-0017 §3.4 — a producer may never be upgraded past what one road
        // to a city wanting its good can carry).
        build.disabled = s.money < PRODUCER_BUILD_COST;
        // A ruin answers neither refusal: its line is baked into the markup
        // above, and rewriting it per frame would erase it.
        if (closed) return;
        const road = !canUpgrade(s, n.id);
        upgrade.disabled = road || s.money < cost;
        why.textContent = road
          // Seit RFC-0035 ist das keine Sackgasse mehr, sondern ein Kaufhinweis:
          // die Decke hängt an den Spuren der gebauten Straße.
          ? "Die Straße ist der Engpass, nicht das Werk — eine Spur mehr auf der Strecke zu einer Stadt, die diese Ware will, schafft Platz."
          : s.money < cost ? "Noch nicht genug Geld." : "";
      });
      return;
    }

    if (n.kind === "transfer") {
      const incoming = s.routes.filter((r) => r.to === n.id).length;
      const outgoing = s.routes.filter((r) => r.from === n.id).length;
      // Before any refinery exists, a "Werkzeug weiterleiten" button is a
      // promise buildRoute() refuses — the hub can never have the good.
      const held = listedGoods(s);
      // The two counts stay in the markup: routes cannot appear or disappear
      // while this node is selected. The stock rows change every frame and go
      // into updaters, or they would freeze at whatever they were.
      const rows = held.map((g) => `
        <div class="kv">
          <span style="color:${goodColor(GOODS[g].token)}">${GOODS[g].name}</span>
          <strong id="t-stock-${g}"></strong>
        </div>`).join("");
      const buttons = held.map((g) => `
        <button class="action secondary" id="t-fwd-${g}">${GOODS[g].name} weiterleiten</button>`)
        .join("");

      // The map paints a refinery in the colour of what it makes (RFC-0015
      // §9.2); the card says it in words as well, because a hub that converts
      // and one that does not are otherwise the same object with the same
      // numbers, and colour alone is not a readout.
      const tint = n.refines ? goodColor(GOODS.werkzeug.token) : "var(--transfer)";
      const recipe = n.refines
        ? `<div class="kv"><span>Veredelt</span><strong>${RECIPE.holz} ${GOODS.holz.name}
            + ${RECIPE.erz} ${GOODS.erz.name} → 1 ${GOODS.werkzeug.name}</strong></div>
           <div class="kv"><span>Tempo</span><strong>${REFINERY_RATE} / s</strong></div>`
        : "";

      this.root.innerHTML = `
        <div class="card">
          <div class="card-title"><span class="swatch" style="background:${tint}"></span>${n.name}</div>
          <div class="meter"><i id="t-meter" style="background:${tint}"></i></div>
          <div class="kv"><span>Lager</span><strong id="t-total"></strong></div>
          ${rows}
          ${recipe}
          <div class="kv"><span>Zufuhr</span><strong>${incoming}</strong></div>
          <div class="kv"><span>Abgänge</span><strong>${outgoing}</strong></div>
          ${buttons}
        </div>`;

      const meter = need("t-meter");
      const total = need("t-total");
      const stocks = held.map((g) => [g, need(`t-stock-${g}`)] as const);
      for (const g of held) {
        // A hub holds two goods, so a click on the map cannot say which one a
        // new route should haul. The card asks instead.
        need(`t-fwd-${g}`).addEventListener("click", () => {
          this.ui.linkFrom = n.id;
          this.ui.linkGood = g;
          this.invalidate();
        });
      }
      this.updaters.push(() => {
        const stored = totalStock(n);
        const f = stored / n.cap;
        meter.style.width = `${clamp(f, 0, 1) * 100}%`;
        meter.style.background = f > 0.96 ? palette().error : tint;
        total.textContent = `${Math.floor(stored)} / ${n.cap}`;
        for (const [g, el] of stocks) el.textContent = String(Math.floor(n.stock[g]));
      });
      return;
    }

    const supplied = s.routes.filter((r) => r.to === n.id).length;
    // Every good gets a line, including the ones this city does not want: „Erz —"
    // is the sentence the whole of RFC-0016 is about, and a list that silently
    // omitted it would leave the player to infer the rule from a missing row.
    const eats = listedGoods(s);
    const rows = eats.map((g) => `
      <div class="kv">
        <span style="color:${goodColor(GOODS[g].token)}">${GOODS[g].name}</span>
        <strong id="c-good-${g}"></strong>
      </div>`).join("");

    this.root.innerHTML = `
      <div class="card">
        <div class="card-title"><span class="swatch" style="background:var(--city)"></span>${n.name}</div>
        ${rows}
        <div class="meter"><i id="c-meter"></i></div>
        <div class="kv"><span>Rückstand</span><strong id="c-backlog"></strong></div>
        <div class="kv"><span>Vertrauen</span><strong id="c-trust"></strong></div>
        <div class="kv"><span>Beliefert von</span><strong>${supplied}</strong></div>
      </div>`;
    const meter = need("c-meter");
    const backlog = need("c-backlog");
    const trust = need("c-trust");
    const goods = eats.map((g) => [g, need(`c-good-${g}`)] as const);

    this.updaters.push(() => {
      const p = palette();
      const wants = wantedGoods(n);
      for (const [g, el] of goods) {
        if (!wants.includes(g)) {
          el.textContent = "—";
          el.style.color = "";
          continue;
        }
        // What the next full truckload of *this* good would be worth per unit:
        // the part the city is waiting for sells at full price, the rest at
        // GLUT_FACTOR (RFC-0008). Per good since RFC-0016 — one number for the
        // whole city would have averaged away the only question the player has.
        const owed = n.backlog[g];
        const paid = Math.min(TRUCK_CAPACITY, owed)
          + Math.max(0, TRUCK_CAPACITY - owed) * GLUT_FACTOR;
        const worth = paid / TRUCK_CAPACITY;
        el.textContent = `${rate(n.demand[g])} · ${int(owed)} · ${pct(worth)}`;
        el.style.color = worth < 0.6 ? p.warning : "";
      }

      // The meter measures against the cap this city can actually reach: it is
      // per good, so a two-good city that owes 200 of each is full, not double.
      const owed = totalBacklog(n);
      meter.style.width =
        `${clamp(owed / (BACKLOG_CAP * Math.max(1, wants.length)), 0, 1) * 100}%`;
      meter.style.background = n.trust > 0.85 ? p.success : n.trust > 0.65 ? p.warning : p.error;
      backlog.textContent = `${int(owed)} Einheiten · ${rate(totalDemand(n))}`;
      trust.textContent = pct(n.trust);
    });
  }

  private buildRoute(s: GameState, id: number): void {
    const r = findRoute(s, id);
    if (!r) {
      this.ui.selection = null;
      return this.buildEmpty();
    }
    const from = sourceOf(s, r);
    const to = sinkOf(s, r);
    const good = GOODS[r.good];

    // The lane button only exists once congestion does. Without the milestone
    // congestionFactor() returns 1 whatever the traffic, so a lane would be a
    // purchase with no effect — buyLane() refuses it for the same reason.
    // Its price cannot change while one route is selected, so it belongs in the
    // markup; only `disabled` is touched per frame.
    const widenable = hasMilestone(s, "verkehr");
    const lane = widenable
      ? `<button class="action secondary" id="r-lane">Spur ausbauen — ${euro(laneCost(narrowest(s, r).length))}</button>`
      : "";

    // Umlauf and Durchsatz are live now: both depend on how many trucks are on
    // the route, so a value computed here would freeze at whatever it was when
    // the route was selected.
    this.root.innerHTML = `
      <div class="card">
        <div class="card-title">
          <span class="swatch" style="background:${goodColor(good.token)}"></span>${from.name}
        </div>
        <div class="kv" style="margin:-6px 0 8px">
          <span>↓ ${good.name}</span><strong>${to.name}</strong>
        </div>
        <div class="kv"><span>Umlauf</span><strong id="r-trip"></strong></div>
        <div class="kv"><span>Lkw</span><strong id="r-count"></strong></div>
        <div class="kv"><span>Spuren</span><strong id="r-lanes"></strong></div>
        <div class="kv"><span>Tempo</span><strong id="r-speed"></strong></div>
        <div class="kv"><span>Durchsatz</span><strong id="r-flow"></strong></div>
        <div class="kv"><span>Unterhalt</span><strong id="r-upkeep"></strong></div>
        <button class="action" id="r-buy">Lkw kaufen — ${euro(TRUCK_COST)}</button>
        <button class="action secondary" id="r-unbuy">
          Lkw abgeben — ${euro(Math.round(TRUCK_REFUND * TRUCK_COST))}
        </button>
        ${lane}
        <button class="action secondary" id="r-sell">Strecke abreißen</button>
      </div>`;

    const trip = need("r-trip");
    const count = need("r-count");
    const lanes = need("r-lanes");
    const speed = need("r-speed");
    const flow = need("r-flow");
    const upkeep = need("r-upkeep");
    const buy = need<HTMLButtonElement>("r-buy");
    const unbuy = need<HTMLButtonElement>("r-unbuy");
    // Every lane on a road costs the same — laneCost() reads the length, not the
    // width — so the baked-in label stays true and no rebuild is needed.
    const widen = widenable ? need<HTMLButtonElement>("r-lane") : null;

    buy.addEventListener("click", () => { this.runCommand({ type: "buyTruck", routeId: r.id }); });
    unbuy.addEventListener("click", () => { this.runCommand({ type: "sellTruck", routeId: r.id }); });
    widen?.addEventListener("click", () => {
      this.runCommand({ type: "buyLane", corridorId: narrowest(s, r).id });
    });
    need("r-sell").addEventListener("click", () => {
      this.runCommand({ type: "sellRoute", routeId: r.id });
      this.ui.selection = null;
      this.invalidate();
    });

    this.updaters.push(() => {
      const n = trucksOnRoute(s, r.id).length;
      const closed = closedRoute(s) === r.id;
      const factor = routeFactor(s, r, corridorLoad(s), hasMilestone(s, "verkehr"))
        * (closed ? CLOSURE_FACTOR : 1);
      const p = palette();
      count.textContent = String(n);
      // What the road holds, next to how wide it is: "2 (trägt 3,1 Lkw)" is the
      // whole break-even in one line, and it is the number the button changes.
      // The narrowest section, because that is the one the lane button widens
      // and the one the jam comes from (RFC-0033 §5).
      const tight = narrowest(s, r);
      lanes.textContent = `${tight.lanes} (trägt ${routeCapacity(tight.length, tight.lanes).toFixed(1)} Lkw)`;
      trip.textContent = `${roundTripAt(routeLength(s, r), factor).toFixed(1)} s`;
      // The accessible half of the jam signal: the map says "something is wrong
      // here" in colour, this says "55 %" in text. The word matters as much as
      // the number — a jam and a closure both read red, and only one of them is
      // answered by selling a truck.
      speed.textContent = closed ? `${pct(factor)} · gesperrt` : pct(factor);
      const level = jamLevel(factor);
      speed.style.color = level === "clear" ? "" : level === "warn" ? p.warning : p.error;
      flow.textContent = rate(throughputAt(routeLength(s, r), n, factor));
      // One line for both bills, as the daily upkeep event charges them.
      upkeep.textContent = `${euro(n * TRUCK_UPKEEP + laneUpkeep(tight.length, tight.lanes))} / Tag`;
      buy.disabled = s.money < TRUCK_COST;
      // Disabled rather than absent (RFC-0018 §9.1): the card is built once per
      // selection, so a button that comes and goes with `n` would need an
      // invalidate() on the frame the count crosses zero.
      unbuy.disabled = n === 0;
      if (widen) widen.disabled = s.money < laneCost(narrowest(s, r).length);
    });
  }
}
